import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  Grid,
  TextField,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ja } from 'date-fns/locale';
import { FilterList as FilterListIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { text as textColors } from '../../utils/darkModeAdapter';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function UserLogs() {
  const theme = useTheme();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    userId: '',
    startDate: null,
    endDate: null,
    featureName: '',
    period: 7,
  });
  const [users, setUsers] = useState([]);
  const [features, setFeatures] = useState([]);

  useEffect(() => {
    fetchUsers();
    fetchFeatures();
    fetchData();
  }, []);

  useEffect(() => {
    fetchData();
  }, [filters]);

  const fetchUsers = async () => {
    try {
      console.log('Fetching users list');
      const response = await axios.get(`${API_BASE_URL}/admin/users`, {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      console.log('Received users response:', response.data);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
    }
  };

  const fetchFeatures = async () => {
    try {
      console.log('Fetching features list');
      const response = await axios.get(`${API_BASE_URL}/admin/activity/features`, {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      console.log('Received features response:', response.data);
      setFeatures(response.data);
    } catch (error) {
      console.error('Failed to fetch features:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      console.log('Fetching logs with filters:', filters);
      
      // パラメータオブジェクトを作成
      const params = {};
      if (filters.userId) params.user_id = filters.userId;
      if (filters.featureName) params.feature_name = filters.featureName;
      if (filters.startDate) params.start_date = filters.startDate.toISOString();
      if (filters.endDate) params.end_date = filters.endDate.toISOString();
      if (filters.period) params.days = filters.period;
      
      const response = await axios.get(`${API_BASE_URL}/admin/activity/logs`, {
        params,
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });
      
      console.log('Received logs response:', response.data);
      setLogs(response.data.logs);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
      if (error.response) {
        console.error('Error response:', error.response.data);
        console.error('Error status:', error.response.status);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const resetFilters = () => {
    setFilters({
      userId: '',
      startDate: null,
      endDate: null,
      featureName: '',
      period: 7,
    });
  };

  const getFeatureColor = (featureName) => {
    const colors = {
      'recruit_plan': '#2196f3',
      'interview': '#4caf50',
      'media_selection': '#ff9800',
      'job_post': '#9c27b0',
    };
    return colors[featureName] || textColors(theme).muted;
  };

  const getUserDisplayName = (userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return `ID: ${userId}`;
    return user.company_name ? `${user.company_name} (${user.email})` : user.email;
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ja}>
      <Box sx={{ p: 3 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12}>
                <Typography variant="h6" component="div" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <FilterListIcon />
                  フィルター
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>ユーザー</InputLabel>
                  <Select
                    value={filters.userId}
                    onChange={(e) => handleFilterChange('userId', e.target.value)}
                    label="ユーザー"
                  >
                    <MenuItem value="">全て</MenuItem>
                    {users.map((user) => (
                      <MenuItem key={user.id} value={user.id}>
                        {user.company_name ? `${user.company_name} (${user.email})` : user.email}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>機能</InputLabel>
                  <Select
                    value={filters.featureName}
                    onChange={(e) => handleFilterChange('featureName', e.target.value)}
                    label="機能"
                  >
                    <MenuItem value="">全て</MenuItem>
                    {features.map((feature) => (
                      <MenuItem key={feature} value={feature}>
                        {feature}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="開始日"
                  value={filters.startDate}
                  onChange={(date) => handleFilterChange('startDate', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <DatePicker
                  label="終了日"
                  value={filters.endDate}
                  onChange={(date) => handleFilterChange('endDate', date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={2}>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Tooltip title="フィルターをリセット">
                    <IconButton onClick={resetFilters} size="small">
                      <RefreshIcon />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <TableContainer component={Paper} sx={{ maxHeight: 'calc(100vh - 300px)', overflow: 'auto' }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>日時</TableCell>
                <TableCell>ユーザー</TableCell>
                <TableCell>機能</TableCell>
                <TableCell>アクション</TableCell>
                <TableCell>ページパス</TableCell>
                <TableCell align="right">時間（分）</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : logs.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="body2" color="textSecondary">
                      ログデータがありません
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                logs.map((log) => (
                  <TableRow key={log.id} hover>
                    <TableCell>
                      {(() => {
                        // デバッグ用：元のタイムスタンプ形式を確認
                        console.log('Original timestamp:', log.start_time);
                        
                        // UTC時刻として明示的に解釈して日本時刻に変換
                        let date;
                        if (typeof log.start_time === 'string') {
                          // 文字列の場合、UTC時刻として強制的に解釈
                          if (log.start_time.endsWith('Z')) {
                            // 既にUTC形式
                            date = new Date(log.start_time);
                          } else {
                            // Zが付いていない場合、UTC時刻として解釈
                            date = new Date(log.start_time + 'Z');
                          }
                        } else {
                          date = new Date(log.start_time);
                        }
                        
                        console.log('Parsed date object:', date);
                        console.log('JST time:', date.toLocaleString('ja-JP', { timeZone: 'Asia/Tokyo' }));
                        
                        return new Intl.DateTimeFormat('ja-JP', {
                          timeZone: 'Asia/Tokyo',
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit',
                          hour12: false
                        }).format(date);
                      })()}
                    </TableCell>
                    <TableCell>{getUserDisplayName(log.user_id)}</TableCell>
                    <TableCell>
                      <Chip
                        label={log.feature_name}
                        size="small"
                        sx={{
                          backgroundColor: getFeatureColor(log.feature_name),
                          color: 'white',
                        }}
                      />
                    </TableCell>
                    <TableCell>{log.action_type}</TableCell>
                    <TableCell>{log.page_path}</TableCell>
                    <TableCell align="right">
                      {log.duration ? Math.round(log.duration / 60) : '-'}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </LocalizationProvider>
  );
}

export default UserLogs; 